import { campaignMediums, campaignStates } from 'utils/campaigns';
import { createDropdownOption } from 'utils/helpers';
import { CampaignColumns } from './customWidgetSettings/campaignColumns';
import { InvertNumberColor } from './customWidgetSettings/invertNumberColor';
import NumberRange from './customWidgetSettings/numberRange';
import NumberRangeTheme from './customWidgetSettings/numberRangeTheme';
import TrendColors from './customWidgetSettings/trendColors';
import TrendDirection, { trendDirections } from './customWidgetSettings/trendDirection';
import * as t from './widgetSettingsTypes';

/**
 * Definition of chart settings.
 * Basic Structure { label, helpText, type, props, getValue, onChange, getInput(For custom inputs), shouldDisplay }
 * @param { String } label: The label of the input.
 * @param { String } helpText: Help text to display.
 * @param { String } type: The input type. See ./widgetSettingsTypes.js for types.
 * @param { Object } props: Additional props to pass to the input.
 * @param { Function } getValue: Function to get the value for the input from the given settings.
 * @param { Function } onChange: Function that returns an updated settings with the new value.
 * @param { Function } getInput: (For custom inputs) Returns the input.
 *                               Object { onChange, value, ...props } get passed in.
 * @param { Function } shouldDisplay: Function to conditionally display settings. Return boolean.
 */

export const BASE_SETTINGS = {
  getValue: () => {},
  onChange: () => {},
};

/** Chart settings divider. */
export const DIVIDER = { ...BASE_SETTINGS, type: t.DIVIDER };

/** X Axis Label */
export const X_AXIS = {
  label: 'X-Axis Label',
  type: t.TEXT,
  getValue: (settings) => {
    if (settings && settings.xAxis && settings.xAxis.title) {
      return settings.xAxis.title.text;
    }
    return '';
  },
  onChange: (value) => ({
    xAxis: {
      title: {
        text: value,
      },
    },
  }),
};

/** Y Axis Label */
export const Y_AXIS = {
  label: 'Y-Axis Label',
  type: t.TEXT,
  getValue: (settings) => {
    if (settings && settings.yAxis && settings.yAxis.title) {
      return settings.yAxis.title.text;
    }
    return '';
  },
  onChange: (value) => ({
    yAxis: {
      title: {
        text: value,
      },
    },
  }),
};

/** Invert Number Color */
export const INVERT_NUMBER_COLOR = {
  label: 'Coloring',
  helpText: 'Choose between coloring the number or the backgound of the widget.',
  type: t.CUSTOM,
  getInput: ({ onChange, value, ...props } /*: { onChange: () => void, value: string } */) => (
    <InvertNumberColor onChange={onChange} value={value} {...props} />
  ),
  getValue: (settings) => {
    if (settings) {
      return settings.invertColors;
    }
    return '';
  },
  prependDivider: true,
  onChange: (value) => ({ invertColors: value }),
};

/** Toggle Number Trend */
export const TREND = {
  label: 'Trend',
  helpText: 'Display the trend arrow and percent change over the previous time range selected.',
  type: t.TOGGLE,
  getValue: (settings) => {
    if (settings) {
      return settings.showTrend;
    }
    return '';
  },
  onChange: (value) => ({ showTrend: value }),
};

/** Number Trend Direction */
export const TREND_DIRECTION = {
  label: 'Trend Direction',
  helpText:
    'Determine if a trend is positive or negative. You also have the option of choosing your own colors.',
  type: t.CUSTOM,
  getInput: ({ onChange, value, ...props } /*: { onChange: () => void, value: string } */) => (
    <TrendDirection onChange={onChange} value={value} {...props} />
  ),
  getValue: (settings) => {
    if (settings) {
      return settings.trendDirection;
    }
    return '';
  },
  onChange: (value) => ({
    customTrend: value === trendDirections.CUSTOMTREND,
    trendDirection: value,
  }),
  shouldDisplay: (settings) => settings && settings.showTrend && !settings.enableRange,
};

/** Number Trend Colors */
export const TREND_COLORS = {
  label: 'Custom Trend Colors',
  type: t.CUSTOM,
  getInput: ({ onChange, value, ...props } /*: { onChange: () => void, value: string } */) => (
    <TrendColors onChange={onChange} value={value} {...props} />
  ),
  getValue: (settings) => {
    if (settings) {
      return settings.trendColors;
    }
    return '';
  },
  onChange: (value) => ({ trendColors: value }),
  shouldDisplay: (settings) =>
    settings && settings.customTrend && settings.showTrend && !settings.enableRange,
};

/** Toggle Number Range */
export const NUMBER_RANGE = {
  label: 'Range',
  helpText: 'Determine the color of the number within a given range.',
  type: t.TOGGLE,
  getValue: (settings) => {
    if (settings) {
      return settings.enableRange;
    }
    return '';
  },
  prependDivider: true,
  onChange: (value, { customRange, trendDirection } = {}) => ({
    enableRange: value,
    customRange,
    trendDirection,
  }),
};

/** Toggle Number Range */
export const NUMBER_RANGE_THEME = {
  label: 'Theme',
  helpText: 'Theme of the number color from min to max (left to right)',
  type: t.CUSTOM,
  getInput: ({ onChange, value, ...props } /* : { onChange: () => void, value: string } */) => (
    <NumberRangeTheme onChange={onChange} value={value} {...props} />
  ),
  getValue: (settings) => {
    if (settings) {
      return settings.rangeTheme;
    }
    return '';
  },
  onChange: (value) => ({ rangeTheme: value }),
  shouldDisplay: (settings) => settings && settings.enableRange,
};

/** Number Range Thresholds */
export const NUMBER_RANGE_THRESHOLDS = {
  label: 'Thresholds',
  helpText:
    'Determine the color of the number within a particular range. Add new ranges by clicking between each number field. You can adjust the values of the ranges using the input boxes or slider bar.',
  type: t.CUSTOM,
  getInput: ({ onChange, value, ...props } /* : { onChange: () => void, value: string } */) => (
    <NumberRange onChange={onChange} value={value} {...props} />
  ),
  getValue: (settings) => {
    if (settings) {
      return settings.rangeThresholds;
    }
    return '';
  },
  onChange: (value) => ({ rangeThresholds: value }),
  shouldDisplay: (settings) =>
    settings && settings.enableRange && settings.rangeTheme && settings.rangeTheme.customTheme,
};

/** Toggle Number Sparkline */
export const SPARKLINE = {
  label: 'Sparkline',
  type: t.TOGGLE,
  getValue: (settings) => {
    if (settings) {
      return settings.showSparkline;
    }
    return '';
  },
  onChange: (value) => ({ showSparkline: value }),
  prependDivider: true,
  shouldDisplay: ({ metric }) => {
    if (metric && metric.displayType === 'percentage') {
      return false;
    }
    return true;
  },
};

/** Campaign Table Columns */
export const CAMPAIGN_COLUMNS = {
  label: 'Columns',
  type: t.CUSTOM,
  getInput: ({ onChange, value, ...props } /*: { onChange: () => void, value: [] } */) => (
    <CampaignColumns onChange={onChange} value={value} {...props} />
  ),
  getValue: (settings) => {
    if (settings) {
      return settings.columns;
    }
    return [];
  },
  onChange: (value) => ({ columns: value }),
};

/** Campaign Table State */
export const CAMPAIGN_MEDIUM = {
  label: 'Medium',
  type: t.DROPDOWN,
  props: { upward: true },
  options: Object.keys(campaignMediums).map((medium) => {
    const { icon, text, value } = campaignMediums[medium];
    return createDropdownOption(value, text, value, { icon });
  }),
  getValue: (settings) => {
    const ALL = campaignMediums.ALL.value;
    if (settings && settings.fetchParams) {
      return settings.fetchParams.mediumFilter || ALL;
    }
    return ALL;
  },
  onChange: (value) => ({ fetchParams: { mediumFilter: value } }),
};

/** Campaign Table State */
export const CAMPAIGN_STATE = {
  label: 'State',
  type: t.DROPDOWN,
  props: { upward: true },
  options: Object.keys(campaignStates).map((state) => {
    const { icon, text, value } = campaignStates[state];
    return createDropdownOption(value, text, value, { icon });
  }),
  getValue: (settings) => {
    const ALL = campaignStates.ALL.value;
    if (settings && settings.fetchParams) {
      return settings.fetchParams.stateFilter || ALL;
    }
    return campaignStates.ALL.value;
  },
  onChange: (value) => ({ fetchParams: { stateFilter: value } }),
};

/** Markdown Text */
export const MARKDOWN_TEXT = {
  label: 'Text',
  type: t.MARKDOWN,
  onlyInput: true,
  getValue: (settings) => {
    if (settings) {
      return settings.text;
    }
    return '';
  },
  onChange: (value) => ({ text: value }),
};
